import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Checkbox,
  Chip,
  Tooltip,
  Divider,
  FormControlLabel,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import EasyCheckDialog from '../../common/Layout/EasyCheckDialog';
import { EasyCheckButton } from '../../common/Controls';
import { chequeTemplateService } from '../../../services/templates/chequeTemplateService';

const CopyTemplateDialog = ({
  open,
  onClose,
  sourceBank,
  banks = [],
  onComplete,
  onError,
}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) setSelectedIds([]);
  }, [open]);

  const candidateBanks = useMemo(
    () => (banks || []).filter((b) => b.id !== sourceBank?.id),
    [banks, sourceBank]
  );

  const eligibleBanks = useMemo(
    () => candidateBanks.filter((b) => !b.hasChequeTemplate),
    [candidateBanks]
  );

  const allEligibleSelected =
    eligibleBanks.length > 0 && eligibleBanks.every((b) => selectedIds.includes(b.id));
  const someEligibleSelected =
    !allEligibleSelected && eligibleBanks.some((b) => selectedIds.includes(b.id));

  const toggleAll = () => {
    if (allEligibleSelected) setSelectedIds([]);
    else setSelectedIds(eligibleBanks.map((b) => b.id));
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (selectedIds.length === 0 || !sourceBank) return;
    setSubmitting(true);
    try {
      const result = await chequeTemplateService.copyTemplateToBanks(
        sourceBank.id,
        selectedIds
      );
      onComplete?.(result, candidateBanks);
      onClose?.();
    } catch (err) {
      onError?.(err.message || 'Failed to copy template');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  if (!sourceBank) return null;

  return (
    <EasyCheckDialog
      open={open}
      onClose={handleClose}
      title={`Copy Template from ${sourceBank.accountName} - ${sourceBank.accountNumber}`}
      titleIcon={<CopyIcon />}
      variant="large"
      maxWidth="md"
      disableBackdropClick={submitting}
      disableEscapeKeyDown={submitting}
      actions={
        <>
          <EasyCheckButton variant="outlined" onClick={handleClose} disabled={submitting}>
            Cancel
          </EasyCheckButton>
          <EasyCheckButton
            variant="contained"
            onClick={handleSubmit}
            loading={submitting}
            disabled={selectedIds.length === 0 || submitting}
          >
            {`Copy to ${selectedIds.length} account${selectedIds.length === 1 ? '' : 's'}`}
          </EasyCheckButton>
        </>
      }
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Select the bank accounts you want to copy this template to. Accounts that already
          have a template are disabled and cannot be overwritten.
        </Typography>

        {candidateBanks.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No other bank accounts available to copy this template to.
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allEligibleSelected}
                    indeterminate={someEligibleSelected}
                    onChange={toggleAll}
                    disabled={eligibleBanks.length === 0 || submitting}
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={600}>
                    {`Select all eligible (${eligibleBanks.length})`}
                  </Typography>
                }
              />
              <Typography variant="caption" color="text.secondary">
                {`${selectedIds.length} of ${eligibleBanks.length} selected`}
              </Typography>
            </Box>

            <Divider />

            <Stack
              spacing={0}
              sx={{
                maxHeight: 380,
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 0,
              }}
            >
              {candidateBanks.map((bank) => {
                const hasTemplate = !!bank.hasChequeTemplate;
                const checked = selectedIds.includes(bank.id);
                const row = (
                  <Box
                    key={bank.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 1.5,
                      py: 1.25,
                      borderBottom: '1px solid #f0f0f0',
                      backgroundColor: hasTemplate ? '#fafafa' : 'transparent',
                      cursor: hasTemplate || submitting ? 'not-allowed' : 'pointer',
                      '&:hover': {
                        backgroundColor: hasTemplate ? '#fafafa' : '#f5faff',
                      },
                    }}
                    onClick={() => {
                      if (hasTemplate || submitting) return;
                      toggleOne(bank.id);
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      disabled={hasTemplate || submitting}
                      onChange={() => toggleOne(bank.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: hasTemplate ? 'text.disabled' : 'text.primary' }}
                      >
                        {bank.accountName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: hasTemplate ? 'text.disabled' : 'text.secondary' }}
                      >
                        {`${bank.accountNumber}${bank.currency ? ` · ${bank.currency}` : ''}`}
                      </Typography>
                    </Box>
                    <Chip
                      icon={hasTemplate ? <CheckIcon /> : <CancelIcon />}
                      label={hasTemplate ? 'Has template' : 'No template'}
                      size="small"
                      sx={{
                        backgroundColor: hasTemplate ? '#D1FAE5' : '#FEF3C7',
                        color: hasTemplate ? '#059669' : '#d97706',
                        border: `1px solid ${hasTemplate ? '#10b981' : '#f59e0b'}`,
                        fontWeight: 600,
                        '& .MuiChip-icon': {
                          color: hasTemplate ? '#059669' : '#d97706',
                        },
                      }}
                    />
                  </Box>
                );

                return hasTemplate ? (
                  <Tooltip
                    key={bank.id}
                    title="Already has a template — cannot override"
                    placement="left"
                  >
                    {row}
                  </Tooltip>
                ) : (
                  row
                );
              })}
            </Stack>
          </>
        )}
      </Stack>
    </EasyCheckDialog>
  );
};

export default CopyTemplateDialog;
